import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getAuthToken, getAuthType, getUserData } from '../../utils/localStorage';

export type SubscriptionTier = 'free' | 'pro' | 'premium';

export interface UserData {
  user_id: number;
  name: string;
  bio: string;
  date_joined: string;
  email: string;
  headline: string;
  image: string | null;
  is_verified: boolean;
  is_expert: boolean;
  languages: string[];
  phone_number: string | null;
  sebi_qualification: string;
  sebi_reg_number: string;
  user_exists: boolean;
  years_of_xp: number;
  // Expert-specific fields
  call_session_rate?: number;
  chat_session_rate?: number;
  agora_user_id?: string;
  agora_user_uuid?: string;
  total_earnings?: number;
  total_sessions?: number;
}

export interface SubscriptionState {
  tier: SubscriptionTier;
  queriesRemaining: number;
  queriesResetAt: string | null;
  expiresAt: string | null;
}

interface UserState {
  user: UserData | null;
  authToken: string | null;
  authType: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  subscription: SubscriptionState;
}

// Mock user for dev mode (when REACT_APP_SKIP_LOGIN=true)
const mockUser: UserData = {
  user_id: 0,
  name: 'Dev User',
  bio: 'Development test user',
  date_joined: new Date().toISOString(),
  email: 'dev@test.com',
  headline: 'Developer',
  image: null,
  is_verified: true,
  is_expert: false,
  languages: ['English'],
  phone_number: null,
  sebi_qualification: '',
  sebi_reg_number: '',
  user_exists: true,
  years_of_xp: 0,
};

const skipLogin = process.env.REACT_APP_SKIP_LOGIN === 'true';

// Restore auth state from localStorage (for page refreshes)
const storedToken = getAuthToken();
const storedAuthType = getAuthType();
const storedUser = getUserData();

// Default subscription state (free tier)
const defaultSubscription: SubscriptionState = {
  tier: 'free',
  queriesRemaining: 5,
  queriesResetAt: null,
  expiresAt: null,
};

const initialState: UserState = {
  user: skipLogin ? mockUser : storedUser,
  authToken: skipLogin ? 'dev-token' : storedToken,
  authType: skipLogin ? 'dev' : storedAuthType,
  isAuthenticated: skipLogin || !!storedToken,
  isLoading: false,
  subscription: defaultSubscription,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<UserData | null>) => {
      state.user = action.payload;
    },
    updateUser: (state, action: PayloadAction<Partial<UserData>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    clearUser: (state) => {
      state.user = null;
    },
    setAuthToken: (state, action: PayloadAction<string>) => {
      state.authToken = action.payload;
      state.isAuthenticated = true;
    },
    setAuthType: (state, action: PayloadAction<string>) => {
      state.authType = action.payload;
    },
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.authToken = null;
      state.authType = null;
      state.isAuthenticated = false;
      state.subscription = defaultSubscription;
    },
    // Subscription actions
    setSubscription: (state, action: PayloadAction<SubscriptionState>) => {
      state.subscription = action.payload;
    },
    setSubscriptionTier: (state, action: PayloadAction<SubscriptionTier>) => {
      state.subscription.tier = action.payload;
    },
    decrementQueriesRemaining: (state) => {
      if (state.subscription.queriesRemaining > 0) {
        state.subscription.queriesRemaining -= 1;
      }
    },
    resetQueriesRemaining: (state, action: PayloadAction<number>) => {
      state.subscription.queriesRemaining = action.payload;
      state.subscription.queriesResetAt = new Date().toISOString();
    },
  },
});

export const {
  setUser,
  updateUser,
  clearUser,
  setAuthToken,
  setAuthType,
  setAuthenticated,
  setLoading,
  logout,
  setSubscription,
  setSubscriptionTier,
  decrementQueriesRemaining,
  resetQueriesRemaining,
} = userSlice.actions;

export default userSlice.reducer;
